import React, { useState } from "react";
import { AlertTriangle } from "lucide-react";
import BottomSheet from "src/components/app/BottomSheet";
import Input from "src/components/app/Input";
import Button from "src/components/app/Button";
import Alert from "src/components/app/Alert";
import Radio from "src/components/common/Radio";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import {
  pincodeValidationRequest,
  updateAddressRequest,
} from "src/redux/slices/locationSlice";
import { PincodeValidationResponse } from "src/types/location.types";

interface AddMilestoneAddressBottomSheetProps {
  show: boolean;
  onClose: () => void;
  // Called once the address is saved so the caller can close the sheet and
  // refetch the address list.
  onSuccess: () => void;
}

const addressTypeOptions: {
  value: "Home" | "Work" | "Other";
  label: string;
}[] = [
  { value: "Home", label: "Home" },
  { value: "Work", label: "Work" },
  { value: "Other", label: "Other" },
];

const PINCODE_LENGTH = 6;

const AddMilestoneAddressBottomSheet: React.FC<
  AddMilestoneAddressBottomSheetProps
> = ({ show, onClose, onSuccess }) => {
  const dispatch = useAppDispatch();
  const patientDetails = useAppSelector((state) => state.auth.patient.data);
  const [pincode, setPincode] = useState("");
  const [houseNoAndBuilding, setHouseNoAndBuilding] = useState("");
  const [street, setStreet] = useState("");
  const [addressType, setAddressType] = useState<"Home" | "Work" | "Other">(
    "Home",
  );
  const [isSaving, setIsSaving] = useState(false);
  // Errors only after the field has been used, so the empty sheet opens clean.
  const [isPincodeTouched, setIsPincodeTouched] = useState(false);

  const isPincodeValid = pincode.length === PINCODE_LENGTH;

  const pincodeError = !isPincodeTouched
    ? undefined
    : !pincode
      ? "Pincode is required"
      : !isPincodeValid
        ? "Pincode must be 6 digits"
        : undefined;

  // Only digits, capped at 6 — anything else can't be a valid Indian pincode.
  const handlePincodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsPincodeTouched(true);
    setPincode(e.target.value.replace(/\D/g, "").slice(0, PINCODE_LENGTH));
  };

  const addAddress = () => {
    setIsSaving(true);
    const payload: Record<string, unknown> = {
      name: patientDetails?.name,
      pincode: Number(pincode),
      contact_no: patientDetails?.contact_no,
      address_type: addressType,
      address: houseNoAndBuilding,
      street,
      // No map picker in this flow, so we don't have real coordinates.
      latitude: 0,
      longitude: 0,
    };
    if (patientDetails?.active_plan_details?.patient_plan_rel_id) {
      payload.patient_plan_rel_id =
        patientDetails?.active_plan_details?.patient_plan_rel_id;
    }

    new Promise((resolve, reject) => {
      dispatch(updateAddressRequest({ resolve, reject, payload }));
    })
      .then(() => {
        onSuccess();
      })
      .catch((err) => {
        console.error("Error adding address", err);
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const handleSave = () => {
    new Promise<PincodeValidationResponse["data"]>((resolve, reject) => {
      dispatch(
        pincodeValidationRequest({ payload: { pincode }, resolve, reject }),
      );
    })
      .then((res) => {
        if (res?.status === "OK") {
          addAddress();
        } else {
          console.error("Pincode validation error", res);
        }
      })
      .catch((err) => {
        console.error("Error validating pincode", err);
      });
  };

  return (
    <BottomSheet
      title="Add Address"
      show={show}
      handleClose={onClose}
      showCloseButton
    >
      <div className="relative w-full p-4 space-y-4">
        <Alert
          title="Disclaimer"
          description="We need this information to deliver your product."
          variant="warning"
          icon={<AlertTriangle />}
        />

        <Input
          name="pincode"
          type="text"
          inputMode="numeric"
          placeholder="Enter Pincode *"
          fullWidth
          maxLength={PINCODE_LENGTH}
          value={pincode}
          onChange={handlePincodeChange}
          onBlur={() => setIsPincodeTouched(true)}
          error={!!pincodeError}
          helperText={pincodeError}
        />
        <Input
          name="houseNoAndBuilding"
          type="text"
          placeholder="House Number and Building *"
          fullWidth
          value={houseNoAndBuilding}
          onChange={(e) => setHouseNoAndBuilding(e.target.value)}
        />
        <Input
          name="street"
          type="text"
          placeholder="Street Name *"
          fullWidth
          value={street}
          onChange={(e) => setStreet(e.target.value)}
        />

        <div>
          <h2 className="text-sm font-medium mb-2">Address Type</h2>
          <div className="flex items-center gap-4">
            {addressTypeOptions.map((opt) => (
              <div
                key={opt.value}
                className="flex items-center gap-2"
                onClick={() => setAddressType(opt.value)}
              >
                <Radio
                  checked={addressType === opt.value}
                  onChange={() => setAddressType(opt.value)}
                />
                <p className="text-sm text-[var(--text-primary)]">
                  {opt.label}
                </p>
              </div>
            ))}
          </div>
        </div>

        <Button
          rounded="full"
          fullWidth
          disabled={!isPincodeValid || !houseNoAndBuilding || !street}
          loading={isSaving}
          onClick={handleSave}
        >
          Save Address
        </Button>
      </div>
    </BottomSheet>
  );
};

export default AddMilestoneAddressBottomSheet;
